/**
 * MUTAGIM.SHOP — Admin Automation Page (Phase 5)
 * Dashboard de surveillance et contrôle de l'automatisation Playwright
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  ArrowLeft,
  Play,
  RefreshCw,
  Settings,
  AlertTriangle,
  CheckCircle,
  Clock,
  DollarSign,
  Package,
  Truck,
  Camera,
  Activity,
  Bell,
  BellOff,
  Zap,
  Eye,
  Server
} from 'lucide-react';
import { AdminPageWrapper } from '@/components/admin/AdminPageWrapper';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export function AdminAutomationPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  
  // État
  const [status, setStatus] = useState(null);
  const [logs, setLogs] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [balances, setBalances] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);
  const [showModeDialog, setShowModeDialog] = useState(false);
  const [showThresholdDialog, setShowThresholdDialog] = useState(false);
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [thresholds, setThresholds] = useState({ alert: 200, critical: 50 });
  
  // Token admin
  const getAuthHeaders = useCallback(() => {
    const token = localStorage.getItem('admin_token');
    return {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    };
  }, []);
  
  // Charger les données
  const loadData = useCallback(async () => {
    try {
      const headers = getAuthHeaders();
      
      const [statusRes, logsRes, jobsRes, balancesRes, alertsRes] = await Promise.all([
        fetch(`${API_URL}/api/admin/automation/status`, { headers }),
        fetch(`${API_URL}/api/admin/automation/logs?limit=100`, { headers }),
        fetch(`${API_URL}/api/admin/automation/jobs?limit=50`, { headers }),
        fetch(`${API_URL}/api/admin/automation/balances`, { headers }),
        fetch(`${API_URL}/api/admin/automation/alerts?acknowledged=false`, { headers })
      ]);
      
      if (statusRes.ok) setStatus(await statusRes.json());
      if (logsRes.ok) {
        const data = await logsRes.json();
        setLogs(data.logs || []);
      }
      if (jobsRes.ok) {
        const data = await jobsRes.json();
        setJobs(data.jobs || []);
      }
      if (balancesRes.ok) {
        const data = await balancesRes.json();
        setBalances(data.balances || []);
      }
      if (alertsRes.ok) {
        const data = await alertsRes.json();
        setAlerts(data.alerts || []);
      }
      
    } catch (error) {
      console.error('Failed to load automation data:', error);
      toast.error('Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);
  
  useEffect(() => {
    loadData();
    // Rafraîchir toutes les 30 secondes
    const interval = setInterval(loadData, 30000);
    return () => clearInterval(interval);
  }, [loadData]);
  
  // Changer le mode
  const handleModeChange = async (newMode) => {
    if (newMode === 'production') {
      setShowModeDialog(true);
      return;
    }
    
    await updateMode('simulation');
  };
  
  const updateMode = async (mode) => {
    setActionLoading('mode');
    try {
      const res = await fetch(`${API_URL}/api/admin/automation/mode`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify({ mode })
      });
      
      if (res.ok) {
        toast.success(`Mode changé en ${mode.toUpperCase()}`);
        loadData();
      } else {
        toast.error('Erreur lors du changement de mode');
      }
    } catch (error) {
      toast.error('Erreur réseau');
    } finally {
      setActionLoading(null);
      setShowModeDialog(false);
    }
  };
  
  // Déclencher une action
  const triggerAction = async (action) => {
    setActionLoading(action);
    try {
      const res = await fetch(`${API_URL}/api/admin/automation/${action}`, {
        method: 'POST',
        headers: getAuthHeaders()
      });
      
      if (res.ok) {
        toast.success(`Action "${action}" démarrée en arrière-plan`);
        setTimeout(loadData, 2000);
      } else {
        toast.error(`Erreur lors de l'action "${action}"`);
      }
    } catch (error) {
      toast.error('Erreur réseau');
    } finally {
      setActionLoading(null);
    }
  };
  
  // Mettre à jour les seuils
  const updateThresholds = async () => {
    if (!selectedAgent) return;
    
    setActionLoading('thresholds');
    try {
      const res = await fetch(`${API_URL}/api/admin/automation/balances/thresholds`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          agent_name: selectedAgent.agent_name,
          alert_threshold: thresholds.alert,
          critical_threshold: thresholds.critical
        })
      });
      
      if (res.ok) {
        toast.success('Seuils mis à jour');
        loadData();
      } else {
        toast.error('Erreur lors de la mise à jour');
      }
    } catch (error) {
      toast.error('Erreur réseau');
    } finally {
      setActionLoading(null);
      setShowThresholdDialog(false);
    }
  };
  
  // Acquitter une alerte
  const acknowledgeAlert = async (alertId) => {
    try {
      const res = await fetch(`${API_URL}/api/admin/automation/alerts/${alertId}/acknowledge`, {
        method: 'POST',
        headers: getAuthHeaders()
      });
      
      if (res.ok) {
        toast.success('Alerte acquittée');
        loadData();
      }
    } catch (error) {
      toast.error('Erreur réseau');
    }
  };
  
  // Formatage des dates
  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };
  
  // Badge de statut
  const StatusBadge = ({ status }) => {
    const variants = {
      completed: { variant: 'default', icon: CheckCircle, className: 'bg-green-500' },
      running: { variant: 'default', icon: RefreshCw, className: 'bg-blue-500 animate-pulse' },
      failed: { variant: 'destructive', icon: AlertTriangle, className: '' },
      pending: { variant: 'secondary', icon: Clock, className: '' }
    };
    
    const config = variants[status] || variants.pending;
    const Icon = config.icon;
    
    return (
      <Badge variant={config.variant} className={config.className}>
        <Icon className="w-3 h-3 mr-1" />
        {status}
      </Badge>
    );
  };
  
  // Indicateur de solde
  const BalanceIndicator = ({ balance }) => {
    const colors = {
      critical: 'text-red-500 bg-red-50 border-red-200',
      warning: 'text-orange-500 bg-orange-50 border-orange-200',
      healthy: 'text-green-500 bg-green-50 border-green-200'
    };
    
    return (
      <div className={`px-3 py-1 rounded-full border ${colors[balance.status] || colors.healthy}`}>
        <span className="font-mono font-semibold">
          ${balance.current_balance?.toFixed(2) || '0.00'}
        </span>
      </div>
    );
  };
  
  if (loading) {
    return (
      <AdminPageWrapper title="Automatisation Playwright" subtitle="Gestion des agents de sourcing">
        <div className="flex items-center justify-center py-20">
          <RefreshCw className="w-8 h-8 animate-spin text-[#C0000C]" />
        </div>
      </AdminPageWrapper>
    );
  }
  
  return (
    <AdminPageWrapper title="Automatisation Playwright" subtitle="Gestion des agents de sourcing">
      <div data-testid="admin-automation-page">
        {/* Mode Controls */}
        <div className="flex items-center justify-between mb-6 p-4 bg-white/50 dark:bg-zinc-800/50 rounded-lg">
          <div className="flex items-center gap-2">
            <Server className="w-4 h-4" />
            <span className="text-sm font-medium">Mode:</span>
            <Badge 
              variant={status?.mode === 'production' ? 'destructive' : 'secondary'}
              className={status?.mode === 'simulation' ? 'bg-blue-500' : ''}
            >
              {status?.mode?.toUpperCase() || 'SIMULATION'}
            </Badge>
            <Switch
              checked={status?.mode === 'production'}
              onCheckedChange={(checked) => handleModeChange(checked ? 'production' : 'simulation')}
              disabled={actionLoading === 'mode'}
              data-testid="mode-switch"
            />
          </div>
          
          {/* Alertes et Refresh */}
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="icon"
              className="relative"
              data-testid="alerts-button"
            >
              {alerts.length > 0 ? (
                <>
                  <Bell className="w-5 h-5 text-red-400" />
                  <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                    {alerts.length}
                  </span>
                </>
              ) : (
                <BellOff className="w-5 h-5" />
              )}
            </Button>
            
            <Button
              variant="outline"
              size="sm"
              onClick={loadData}
              data-testid="refresh-button"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Actualiser
            </Button>
          </div>
        </div>
      
        {/* Alertes actives */}
        {alerts.length > 0 && (
          <Card className="border-red-200 bg-red-50">
            <CardHeader className="pb-2">
              <CardTitle className="text-red-700 flex items-center gap-2">
                <AlertTriangle className="w-5 h-5" />
                Alertes actives ({alerts.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {alerts.slice(0, 5).map((alert, idx) => (
                  <div 
                    key={idx} 
                    className="flex items-center justify-between bg-white rounded-lg p-3 border border-red-100"
                  >
                    <div>
                      <p className="font-medium text-red-800">{alert.message || alert.type}</p>
                      <p className="text-sm text-red-600">{formatDate(alert.created_at)}</p>
                    </div>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => acknowledgeAlert(alert._id)}
                      data-testid={`acknowledge-alert-${idx}`}
                    >
                      Acquitter
                    </Button>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
        
        {/* Cards actions rapides */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card className="hover:shadow-lg transition-shadow">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between mb-4">
                <div className="p-3 rounded-full bg-blue-100">
                  <Package className="w-6 h-6 text-blue-600" />
                </div>
                <StatusBadge status={status?.last_jobs?.sync_stock?.status || 'pending'} />
              </div>
              <h3 className="font-semibold text-lg">Sync Stock</h3>
              <p className="text-sm text-gray-500 mb-4">Toutes les 6h</p>
              <Button
                className="w-full bg-[#C0000C] hover:bg-[#a0000a]"
                onClick={() => triggerAction('sync-now')}
                disabled={actionLoading === 'sync-now'}
                data-testid="trigger-sync-stock"
              >
                {actionLoading === 'sync-now' ? (
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Play className="w-4 h-4 mr-2" />
                )}
                Forcer sync
              </Button>
            </CardContent>
          </Card>
          
          <Card className="hover:shadow-lg transition-shadow">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between mb-4">
                <div className="p-3 rounded-full bg-purple-100">
                  <Camera className="w-6 h-6 text-purple-600" />
                </div>
                <StatusBadge status={status?.last_jobs?.fetch_qc_photos?.status || 'pending'} />
              </div>
              <h3 className="font-semibold text-lg">Photos QC</h3>
              <p className="text-sm text-gray-500 mb-4">Toutes les 2h</p>
              <Button
                className="w-full"
                variant="outline"
                onClick={() => triggerAction('fetch-qc')}
                disabled={actionLoading === 'fetch-qc'}
                data-testid="trigger-fetch-qc"
              >
                {actionLoading === 'fetch-qc' ? (
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Play className="w-4 h-4 mr-2" />
                )}
                Récupérer
              </Button>
            </CardContent>
          </Card>
          
          <Card className="hover:shadow-lg transition-shadow">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between mb-4">
                <div className="p-3 rounded-full bg-green-100">
                  <Truck className="w-6 h-6 text-green-600" />
                </div>
                <StatusBadge status={status?.last_jobs?.fetch_tracking?.status || 'pending'} />
              </div>
              <h3 className="font-semibold text-lg">Tracking</h3>
              <p className="text-sm text-gray-500 mb-4">Toutes les 4h</p>
              <Button
                className="w-full"
                variant="outline"
                onClick={() => triggerAction('fetch-tracking')}
                disabled={actionLoading === 'fetch-tracking'}
                data-testid="trigger-fetch-tracking"
              >
                {actionLoading === 'fetch-tracking' ? (
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Play className="w-4 h-4 mr-2" />
                )}
                Récupérer
              </Button>
            </CardContent>
          </Card>
          
          <Card className="hover:shadow-lg transition-shadow">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between mb-4">
                <div className="p-3 rounded-full bg-yellow-100">
                  <DollarSign className="w-6 h-6 text-yellow-600" />
                </div>
                <StatusBadge status={status?.last_jobs?.check_balance?.status || 'pending'} />
              </div>
              <h3 className="font-semibold text-lg">Solde Agents</h3>
              <p className="text-sm text-gray-500 mb-4">Quotidien 08:00 UTC</p>
              <Button
                className="w-full"
                variant="outline"
                onClick={() => triggerAction('check-balance')}
                disabled={actionLoading === 'check-balance'}
                data-testid="trigger-check-balance"
              >
                {actionLoading === 'check-balance' ? (
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Play className="w-4 h-4 mr-2" />
                )}
                Vérifier
              </Button>
            </CardContent>
          </Card>
        </div>
        
        {/* Onglets principaux */}
        <Tabs defaultValue="logs" className="space-y-4">
          <TabsList className="grid w-full max-w-md grid-cols-3">
            <TabsTrigger value="logs" data-testid="tab-logs">
              <Activity className="w-4 h-4 mr-2" />
              Logs
            </TabsTrigger>
            <TabsTrigger value="balances" data-testid="tab-balances">
              <DollarSign className="w-4 h-4 mr-2" />
              Soldes
            </TabsTrigger>
            <TabsTrigger value="jobs" data-testid="tab-jobs">
              <Zap className="w-4 h-4 mr-2" />
              Jobs
            </TabsTrigger>
          </TabsList>
          
          {/* Onglet Logs */}
          <TabsContent value="logs">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Activity className="w-5 h-5" />
                  Logs de simulation
                </CardTitle>
                <CardDescription>
                  Historique détaillé des actions — montre exactement ce que le système FERAIT en production
                </CardDescription>
              </CardHeader>
              <CardContent>
                <ScrollArea className="h-[500px]">
                  <div className="space-y-2">
                    {logs.length === 0 ? (
                      <p className="text-gray-500 text-center py-8">Aucun log disponible</p>
                    ) : (
                      logs.map((log, idx) => (
                        <div 
                          key={idx} 
                          className="p-3 bg-gray-50 rounded-lg border border-gray-100 font-mono text-sm"
                          data-testid={`log-entry-${idx}`}
                        >
                          <div className="flex items-center justify-between mb-1">
                            <Badge variant="outline" className="text-xs">
                              {log.action}
                            </Badge>
                            <span className="text-xs text-gray-200">
                              {formatDate(log.timestamp)}
                            </span>
                          </div>
                          <p className={`${log.message?.includes('SIMULATION') ? 'text-blue-600' : 'text-gray-700'}`}>
                            {log.message}
                          </p>
                          {log.details && Object.keys(log.details).length > 0 && (
                            <pre className="mt-2 text-xs text-gray-500 overflow-x-auto">
                              {JSON.stringify(log.details, null, 2)}
                            </pre>
                          )}
                        </div>
                      ))
                    )}
                  </div>
                </ScrollArea>
              </CardContent>
            </Card>
          </TabsContent>
          
          {/* Onglet Soldes */}
          <TabsContent value="balances">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <DollarSign className="w-5 h-5" />
                  Soldes des agents
                </CardTitle>
                <CardDescription>
                  Surveillance des soldes avec seuils d'alerte configurables
                </CardDescription>
              </CardHeader>
              <CardContent>
                {balances.length === 0 ? (
                  <div className="text-center py-8">
                    <p className="text-gray-500 mb-4">Aucun agent configuré</p>
                    <p className="text-sm text-gray-200">
                      Les soldes seront affichés après la première vérification
                    </p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {balances.map((balance, idx) => (
                      <div 
                        key={idx}
                        className="p-4 border rounded-lg hover:shadow-md transition-shadow"
                        data-testid={`balance-card-${balance.agent_name}`}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-4">
                            <div className={`w-3 h-3 rounded-full ${
                              balance.status === 'critical' ? 'bg-red-500' :
                              balance.status === 'warning' ? 'bg-orange-500' : 'bg-green-500'
                            }`} />
                            <div>
                              <h4 className="font-semibold text-lg uppercase">{balance.agent_name}</h4>
                              <p className="text-sm text-gray-500">
                                Dernière vérification: {formatDate(balance.last_checked)}
                              </p>
                            </div>
                          </div>
                          
                          <div className="flex items-center gap-4">
                            <BalanceIndicator balance={balance} />
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                setSelectedAgent(balance);
                                setThresholds({
                                  alert: balance.alert_threshold || 200,
                                  critical: balance.critical_threshold || 50
                                });
                                setShowThresholdDialog(true);
                              }}
                              data-testid={`configure-${balance.agent_name}`}
                            >
                              <Settings className="w-4 h-4 mr-1" />
                              Seuils
                            </Button>
                          </div>
                        </div>
                        
                        <Separator className="my-3" />
                        
                        <div className="grid grid-cols-3 gap-4 text-sm">
                          <div>
                            <p className="text-gray-500">Seuil alerte</p>
                            <p className="font-semibold text-orange-500">
                              ${balance.alert_threshold?.toFixed(2) || '200.00'}
                            </p>
                          </div>
                          <div>
                            <p className="text-gray-500">Seuil critique</p>
                            <p className="font-semibold text-red-500">
                              ${balance.critical_threshold?.toFixed(2) || '50.00'}
                            </p>
                          </div>
                          <div>
                            <p className="text-gray-500">Rechargements</p>
                            <p className="font-semibold">
                              {balance.recharge_history?.length || 0}
                            </p>
                          </div>
                        </div>
                        
                        {/* Historique rechargements */}
                        {balance.recharge_history?.length > 0 && (
                          <div className="mt-4">
                            <p className="text-sm font-medium text-gray-700 mb-2">
                              Derniers rechargements
                            </p>
                            <div className="space-y-1">
                              {balance.recharge_history.slice(-3).reverse().map((recharge, rIdx) => (
                                <div 
                                  key={rIdx}
                                  className="flex items-center justify-between text-sm bg-green-50 rounded px-3 py-2"
                                >
                                  <span className="text-green-700 font-medium">
                                    +${recharge.amount?.toFixed(2)}
                                  </span>
                                  <span className="text-gray-500">
                                    {formatDate(recharge.detected_at)}
                                  </span>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
          
          {/* Onglet Jobs */}
          <TabsContent value="jobs">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Zap className="w-5 h-5" />
                  Historique des jobs
                </CardTitle>
                <CardDescription>
                  Toutes les tâches d'automatisation exécutées
                </CardDescription>
              </CardHeader>
              <CardContent>
                <ScrollArea className="h-[500px]">
                  <div className="space-y-2">
                    {jobs.length === 0 ? (
                      <p className="text-gray-500 text-center py-8">Aucun job exécuté</p>
                    ) : (
                      jobs.map((job, idx) => (
                        <div 
                          key={idx}
                          className="p-3 border rounded-lg hover:bg-gray-50 transition-colors"
                          data-testid={`job-entry-${idx}`}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <Badge variant="outline">{job.action}</Badge>
                              <StatusBadge status={job.status} />
                            </div>
                            <span className="text-sm text-gray-500">
                              {formatDate(job.created_at)}
                            </span>
                          </div>
                          {job.result && (
                            <div className="mt-2 text-sm text-gray-600">
                              {job.result.success ? (
                                <span className="text-green-600">
                                  ✓ {job.result.updated || job.result.photos_fetched || job.result.tracking_fetched || 0} éléments traités
                                </span>
                              ) : (
                                <span className="text-red-600">
                                  ✗ {job.result.error}
                                </span>
                              )}
                            </div>
                          )}
                        </div>
                      ))
                    )}
                  </div>
                </ScrollArea>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
        
        {/* Statut du scheduler */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Clock className="w-5 h-5" />
              Tâches planifiées
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {status?.scheduler?.jobs?.filter(j => j.id?.startsWith('playwright')).map((job, idx) => (
                <div key={idx} className="p-3 bg-gray-50 rounded-lg">
                  <p className="font-medium text-sm">{job.name}</p>
                  <p className="text-xs text-gray-500">
                    Prochain: {job.next_run ? new Date(job.next_run).toLocaleString('fr-FR') : '-'}
                  </p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      
      {/* Dialog confirmation mode production */}
      <Dialog open={showModeDialog} onOpenChange={setShowModeDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="w-5 h-5" />
              Attention - Mode Production
            </DialogTitle>
            <DialogDescription className="pt-4">
              <p className="mb-4">
                En mode <strong>PRODUCTION</strong>, le système effectuera de <strong>vraies actions</strong> sur les sites des agents:
              </p>
              <ul className="list-disc list-inside space-y-2 text-sm">
                <li>Connexion réelle aux comptes agents</li>
                <li>Passage de commandes avec paiement réel</li>
                <li>Modification des données de stock</li>
              </ul>
              <p className="mt-4 font-semibold text-red-600">
                Êtes-vous sûr de vouloir activer le mode production ?
              </p>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowModeDialog(false)}>
              Annuler
            </Button>
            <Button 
              variant="destructive" 
              onClick={() => updateMode('production')}
              disabled={actionLoading === 'mode'}
            >
              {actionLoading === 'mode' && <RefreshCw className="w-4 h-4 mr-2 animate-spin" />}
              Activer Production
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Dialog seuils */}
      <Dialog open={showThresholdDialog} onOpenChange={setShowThresholdDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              Configurer les seuils - {selectedAgent?.agent_name?.toUpperCase()}
            </DialogTitle>
            <DialogDescription>
              Définissez les seuils d'alerte pour le solde de cet agent
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="alert">Seuil d'alerte (orange) - $</Label>
              <Input
                id="alert"
                type="number"
                value={thresholds.alert}
                onChange={(e) => setThresholds(prev => ({ ...prev, alert: parseFloat(e.target.value) }))}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="critical">Seuil critique (rouge) - $</Label>
              <Input
                id="critical"
                type="number"
                value={thresholds.critical}
                onChange={(e) => setThresholds(prev => ({ ...prev, critical: parseFloat(e.target.value) }))}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowThresholdDialog(false)}>
              Annuler
            </Button>
            <Button onClick={updateThresholds} disabled={actionLoading === 'thresholds'}>
              {actionLoading === 'thresholds' && <RefreshCw className="w-4 h-4 mr-2 animate-spin" />}
              Sauvegarder
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      </div>
    </AdminPageWrapper>
  );
}

export default AdminAutomationPage;
